import React from "react";
import { SendHorizonal, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { placeholderLabel } from "../../features/template.placeholders";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const StepTestSend = ({
  placeholders,
  dataPoints,
  setDataPoints,
  testEmail,
  setTestEmail,
  testing,
  sendTest,
  isVerifiedThisSession,
  onTestSuccess,
  onTestError,
}) => {
  const canSend = testEmail.trim() && emailRegex.test(testEmail.trim()) && !testing;

  const handleSend = async () => {
    try {
      await sendTest();
      onTestSuccess?.();
    } catch (err) {
      onTestError?.(err);
    }
  };

  return (
    <div className="space-y-4">
      {isVerifiedThisSession ? (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-success_bg border border-success_border text-success text-xs font-medium">
          <CheckCircle2 size={15} /> Test email sent successfully. You can now finish and save this template.
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-warning_bg border border-warning_border text-warning text-xs font-medium">
          <ShieldAlert size={15} /> A successful test send is required before this template can be saved.
        </div>
      )}

      {placeholders.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-text_secondary">Sample values for dynamic fields</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {placeholders.map((key) => (
              <div key={key}>
                <label className="block text-[11px] font-medium text-text_secondary mb-1">
                  {placeholderLabel(key)}{" "}
                  <span className="font-mono text-[10px] text-text_muted">{`{{${key}}}`}</span>
                </label>
                <input
                  type="text"
                  value={dataPoints[key] ?? ""}
                  onChange={(e) => setDataPoints((p) => ({ ...p, [key]: e.target.value }))}
                  placeholder={`Enter ${placeholderLabel(key)}`}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface_alt text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-text_secondary mb-1.5">
          Recipient Email <span className="text-danger">*</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && canSend) handleSend(); }}
            placeholder="you@example.com"
            disabled={testing}
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-surface_alt text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-success/30 focus:border-success disabled:opacity-60 transition"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-success text-white disabled:opacity-60 shadow-sm flex-shrink-0"
          >
            {testing ? (
              <><Loader2 size={13} className="animate-spin" /> Sending…</>
            ) : (
              <><SendHorizonal size={13} /> Send Test</>
            )}
          </button>
        </div>
        <p className="text-[11px] text-text_muted mt-1.5">
          The template will be rendered with the values above and sent here for validation.
        </p>
      </div>
    </div>
  );
};

export default StepTestSend;
